import type { NamedRepository } from '../repositories/named';
import {
    type MaybeRefOrGetter,
    toValue,
    type Component,
    type InjectionKey,
    type ComputedRef,
    computed,
    type FunctionalComponent,
} from 'vue';
import { usePartialRepository } from './usePartialRepository';

export interface UsePartialConponentOptions {
    injectionKey?:
        | InjectionKey<NamedRepository>
        | string
        | symbol
        | null
        | undefined;
    errorHandler?: (err: Error, attempts: number) => boolean;
    maxAttempts?: MaybeRefOrGetter<number | null | undefined>;
    delay?: MaybeRefOrGetter<number | null | undefined>;
    timeout?: MaybeRefOrGetter<number | null | undefined>;
    suspensible?: MaybeRefOrGetter<boolean | null | undefined>;
    fallback?: MaybeRefOrGetter<Component | string | null | undefined>;
    loadingComponent?: MaybeRefOrGetter<Component | null | undefined>;
    errorComponent?: MaybeRefOrGetter<Component | null | undefined>;
}

export function usePartialComponent(
    partial: MaybeRefOrGetter<Component | string | undefined | null>,
    options: UsePartialConponentOptions = {}
): ComputedRef<Component> {
    const repository = usePartialRepository(options.injectionKey);

    return computed(() => {
        const p = toValue(partial);
        if (typeof p === 'string') {
            return repository.asyncComponent(p, {
                errorHandler: options.errorHandler,
                maxAttempts: toValue(options.maxAttempts) ?? undefined,
                delay: toValue(options.delay) ?? undefined,
                timeout: toValue(options.timeout) ?? undefined,
                suspensible: toValue(options.suspensible) ?? undefined,
                fallback: toValue(options.fallback),
                loadingComponent:
                    toValue(options.loadingComponent) ?? undefined,
                errorComponent: toValue(options.errorComponent) ?? undefined,
            });
        } else if (p) {
            return p;
        } else {
            const NullPartial: FunctionalComponent = (_, { slots }) =>
                slots.default ? slots.default() : undefined;
            return (toValue(options.fallback) ?? NullPartial) as Component;
        }
    });
}
